import Money from "../ui/Money"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { SpinnerText } from "../ui/Spinner"
import { FiChevronDown } from "react-icons/fi"
import {
  getPatentFeeBreakdown,
  updatePatentAdministrativeFees,
  updatePatentRefundDeclarations,
  PatentFeeBreakdown,
  PatentFeeGroup,
  PatentRefundDeclarations
} from "../../services/stripePaymentService"
import { formatApiError } from "../../utils/formatApiError"

// Shared fee overview (SCRUM-55), used both as a step in the patent form
// (selection) and on the payment page (final review before submitting).
// A single implementation keeps the two in sync instead of duplicating
// the fetch/update logic twice.
//
// Structure and labelling mirror the real EPO "Online-Einreichung 2.0"
// payment step (Angaben zur Zahlung / Angaben zur Rückerstattung /
// Verfahrensgebühren / Verwaltungsgebühren / Jahresgebühren /
// Erstreckungs-/Validierungsgebühren), translated to English. Only
// "Administrative fees" (certified copy) and "Refund details" are
// actually interactive; the rest is shown for structural parity since
// we either always charge them (001/002/005e) or don't compute an
// amount for them yet (006/013/015/015e/501, annual fees).
export default function PatentFeesSection({
  applicationId,
  disabled = false,
  title = "Fees",
  fixHref
}: {
  applicationId: string | null
  disabled?: boolean
  title?: string
  // Where the customer can correct the application when the fees cannot be worked out
  fixHref?: string
}) {
  const [feeBreakdown, setFeeBreakdown] = useState<PatentFeeBreakdown | null>(null)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The server refused with a message the customer can act on (a document to upload again)
  const [fixable, setFixable] = useState(false)

  useEffect(() => {
    if (!applicationId) {
      setFeeBreakdown(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)
    setFixable(false)

    getPatentFeeBreakdown(applicationId)
      .then((result) => {
        if (!cancelled) setFeeBreakdown(result)
      })
      .catch((loadError) => {
        if (!cancelled) {
          setFeeBreakdown(null)
          setFixable((loadError as any)?.status === 400)
          setError(
            formatApiError(loadError, "The patent fees could not be loaded. Please reload the page and try again.")
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [applicationId])

  async function handleToggleCertifiedCopy(checked: boolean) {
    if (!applicationId || updating || disabled) return

    setUpdating(true)
    setError(null)

    try {
      setFeeBreakdown(await updatePatentAdministrativeFees(applicationId, checked))
    } catch (updateError) {
      setError(
        formatApiError(
          updateError,
          "The administrative fees could not be updated. Please reload the page and try again."
        )
      )
    } finally {
      setUpdating(false)
    }
  }

  async function handleToggleRefundDeclaration(
    field: keyof PatentRefundDeclarations,
    checked: boolean
  ) {
    if (!applicationId || updating || disabled || !feeBreakdown) return

    setUpdating(true)
    setError(null)

    try {
      setFeeBreakdown(
        await updatePatentRefundDeclarations(applicationId, {
          ...feeBreakdown.refundDeclarations,
          [field]: checked
        })
      )
    } catch (updateError) {
      setError(
        formatApiError(
          updateError,
          "The refund details could not be updated. Please reload the page and try again."
        )
      )
    } finally {
      setUpdating(false)
    }
  }

  if (!applicationId) {
    return (
      <div className="text-sm text-gray-500">
        Save the application (fill in a reference number or title) to see the fee overview.
      </div>
    )
  }

  if (loading) {
    return <div className="text-sm text-gray-500"><SpinnerText>Loading fee overview...</SpinnerText></div>
  }

  if (!feeBreakdown) {
    return error ? (
      <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <p>{error}</p>
        {fixable && fixHref && (
          <Link to={fixHref} className="mt-2 inline-block font-medium underline">
            Go to the application
          </Link>
        )}
      </div>
    ) : null
  }

  return (
    <div>
      <div className="mb-2 text-sm font-semibold text-gray-900">{title}</div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="divide-y divide-gray-200 border-y border-gray-200">
        <FeeGroupSection label="Payment details">
          <div className="flex items-start gap-3 rounded border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <span>{feeBreakdown.paymentNote}</span>
          </div>
        </FeeGroupSection>

        <FeeGroupSection label="Refund details">
          <div className="flex flex-col gap-3">
            <label
              className={`flex items-start gap-3 text-sm ${
                disabled ? "text-gray-400" : "text-gray-700"
              }`}
            >
              <input
                type="checkbox"
                checked={feeBreakdown.refundDeclarations.refundToEpoDepositAccount}
                disabled={updating || disabled}
                onChange={(e) =>
                  handleToggleRefundDeclaration("refundToEpoDepositAccount", e.target.checked)
                }
                className="mt-0.5 h-4 w-4 accent-blue-800"
              />
              Any refund to the deposit account at the EPO
            </label>

            <label
              className={`flex items-start gap-3 text-sm ${
                disabled ? "text-gray-400" : "text-gray-700"
              }`}
            >
              <input
                type="checkbox"
                checked={feeBreakdown.refundDeclarations.searchFeeRefundRequested}
                disabled={updating || disabled}
                onChange={(e) =>
                  handleToggleRefundDeclaration("searchFeeRefundRequested", e.target.checked)
                }
                className="mt-0.5 h-4 w-4 accent-blue-800"
              />
              Refund of the search fee is requested because an earlier search report was
              prepared by the EPO (Article 9(2) RFees)
            </label>
          </div>
        </FeeGroupSection>

        <FeeGroupSection
          label={feeBreakdown.procedureFees.label}
          total={feeBreakdown.procedureFees.total}
          currency={feeBreakdown.currency}
        >
          <FeeTable group={feeBreakdown.procedureFees} currency={feeBreakdown.currency} />
        </FeeGroupSection>

        <FeeGroupSection
          label={feeBreakdown.serviceFees.label}
          total={feeBreakdown.serviceFees.total}
          currency={feeBreakdown.currency}
        >
          <FeeTable group={feeBreakdown.serviceFees} currency={feeBreakdown.currency} />
        </FeeGroupSection>

        {feeBreakdown.deferredFees && (
          <FeeGroupSection label={feeBreakdown.deferredFees.label} total={feeBreakdown.deferredFees.total} currency={feeBreakdown.currency}>
            <FeeTable group={feeBreakdown.deferredFees} currency={feeBreakdown.currency} />
          </FeeGroupSection>
        )}
        <FeeGroupSection
          label={feeBreakdown.administrativeFees.label}
          total={feeBreakdown.administrativeFees.total}
          currency={feeBreakdown.currency}
        >
          <FeeTable
            group={feeBreakdown.administrativeFees}
            currency={feeBreakdown.currency}
            onToggle={(checked) => handleToggleCertifiedCopy(checked)}
            toggleDisabled={updating || disabled}
          />
        </FeeGroupSection>

        <FeeGroupSection label={feeBreakdown.annualFees.label}>
          <FeeTable group={feeBreakdown.annualFees} currency={feeBreakdown.currency} />
        </FeeGroupSection>

        <FeeGroupSection label={feeBreakdown.extensionValidationFees.label}>
          {feeBreakdown.extensionValidationFees.states.length === 0 ? (
            <p className="text-sm text-gray-600">No extension/validation states selected</p>
          ) : (
            <ul className="list-disc pl-5 text-sm text-gray-600">
              {feeBreakdown.extensionValidationFees.states.map((state) => (
                <li key={state}>{state}</li>
              ))}
            </ul>
          )}
        </FeeGroupSection>
      </div>

      <div className="mt-4 flex items-center justify-between border-t-2 border-gray-300 pt-3 text-sm font-semibold text-gray-900">
        <span>Total</span>
        <span className="tabular-nums">{formatFee(feeBreakdown.total, feeBreakdown.currency)}</span>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        The EPO fees shown are the standard amounts; page and claims
        surcharges are settled separately by the EPO.
      </p>
    </div>
  )
}

function FeeGroupSection({
  label,
  total,
  currency,
  children
}: {
  label: string
  total?: number
  currency?: string
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-gray-900">{label}</span>

        <span className="flex items-center gap-3">
          {total !== undefined && currency && (
            <span className="text-sm font-medium tabular-nums text-gray-700">
              {formatFee(total, currency)}
            </span>
          )}
          <FiChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>

      {open && <div className="pb-4">{children}</div>}
    </div>
  )
}

function formatFee(amount: number, currency: string) {
  if (currency === "EUR") return <Money amount={amount} />
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency
    }).format(amount)
  } catch {
    return `${amount} ${currency}`
  }
}

function FeeTable({
  group,
  currency,
  onToggle,
  toggleDisabled
}: {
  group: PatentFeeGroup
  currency: string
  onToggle?: (checked: boolean) => void
  toggleDisabled?: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <th className="w-8 py-2"></th>
            <th className="py-2">Fee code and description</th>
            <th className="py-2 text-right">Amount in {currency}</th>
            <th className="py-2 text-right">Reduction</th>
            <th className="py-2 text-right">Quantity</th>
            <th className="py-2 text-right">Amount due</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {group.lines.map((line) => (
            <tr key={line.code} className="text-gray-700">
              <td className="py-2">
                <input
                  type="checkbox"
                  checked={line.selected}
                  disabled={!line.selectable || toggleDisabled}
                  onChange={(e) => onToggle?.(e.target.checked)}
                  className="h-4 w-4 accent-blue-800 disabled:cursor-not-allowed"
                />
              </td>
              <td className="py-2">
                {line.code} - {line.label}
              </td>
              <td className="py-2 text-right tabular-nums">
                {line.amount !== null ? formatFee(line.amount, currency) : "—"}
              </td>
              <td className="py-2 text-right tabular-nums text-gray-400">—</td>
              <td className="py-2 text-right tabular-nums">
                {line.amount !== null ? "1" : "—"}
              </td>
              <td className="py-2 text-right font-medium tabular-nums">
                {line.amount !== null && line.selected ? formatFee(line.amount, currency) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
